import { useState, useEffect, useCallback } from 'react';

export interface UseSimulatedProgressProps {
  maxProgress: number;
  countdownTimeSecs: number;
}

export interface UseSimulatedProgressResponse {
  progress: number;
  startSimulatedProgress: () => void;
  stopSimulatedProgress: () => void;
}

const useSimulatedFeedback = ({
  maxProgress,
  countdownTimeSecs,
}: UseSimulatedProgressProps): UseSimulatedProgressResponse => {
  const [progress, setProgress] = useState<number>(maxProgress);
  const [timer, setTimer] = useState<number>(0);
  const [progressSteps, setProgressSteps] = useState<number[] | null>(null);

  useEffect(() => {
    // Exit the useEffect when count reaches 0
    if (progress >= maxProgress - 1) {
      return;
    }

    if (!progressSteps) {
      return;
    }

    // Set up the interval to decrement count every second
    const intervalId = setInterval(() => {
      setTimer(prevTimer => {
        const newTimer = prevTimer - 1;
        const nextProgress = Math.min(progress + progressSteps?.[newTimer] ?? 0, maxProgress - 1);
        const steps = nextProgress - progress;
        // Increment the progress one at a time to make it smooth
        const incrementStep = () => {
          setProgress(currentProgress => {
            if (currentProgress + 1 < nextProgress) {
              setTimeout(incrementStep, 500 / steps);
            }
            return currentProgress + 1;
          });
        };
        incrementStep();
        return newTimer;
      });
    }, 1000);

    // Clean up the interval when the component unmounts or count reaches 0
    return () => clearInterval(intervalId);
  }, [timer, progressSteps, progress, maxProgress]);

  const generateProgressSteps = (timeSecs: number, numSteps: number): number[] => {
    const randomList: number[] = [];
    for (let i = 0; i < timeSecs; i++) {
      const randomNumber = (Math.random() * numSteps) / timeSecs; // Generate a random number between 0 and (total / size)
      randomList.push(randomNumber);
    }

    // Adjust the list to ensure the sum equals the desired total
    const currentSum = randomList.reduce((sum, num) => sum + num, 0);
    const scale = numSteps / currentSum; // Compute scaling factor to adjust sum to the desired total

    const adjustedList = randomList.map(num => num * scale);

    // Round the numbers to integers (optional step)
    const roundedList = adjustedList.map(num => Math.round(num));

    // Return the final list
    return roundedList;
  };

  const startSimulatedProgress = useCallback(() => {
    setProgress(0);
    setProgressSteps(generateProgressSteps(countdownTimeSecs, maxProgress - 1));
    setTimer(countdownTimeSecs);
  }, [setProgress, setProgressSteps, setTimer, countdownTimeSecs, maxProgress]);

  const stopSimulatedProgress = useCallback(() => {
    setProgress(maxProgress);
  }, [setProgress, maxProgress]);

  return {
    progress,
    startSimulatedProgress,
    stopSimulatedProgress,
  };
};

export default useSimulatedFeedback;
